import { CheckboxSelectionCallback, ValueGetterParams } from '@ag-grid-community/core';
import { Task, TASK_PSE_STATUS, TASK_PSE_SYNC_STATUS, TaskPseStatus, TaskType } from '../model/task.model';
import { getDistance } from 'ol/sphere';
import { Location, UserModel } from '../../../model/general.model';
import { userOptionLabel } from '../../../utils/user.utils';
import { DATE_TIME_FORMAT, getLocalDate } from '../../../utils/date.utils';
import { OrderCellComponent } from '../components/_grid/order-cell/order-cell.component';
import { ACTION_COLUMN } from '../../../config/grid.config';
import { TasksGridActionsComponent } from '../components/_grid/tasks-grid-actions/tasks-grid-actions.component';
import { TranslateService } from '@ngx-translate/core';
import { SpanModel } from '../../../model/span.model';

export const getTaskStatus = (value: ValueGetterParams): string => {
  return "TASKS.STATUS." + (!!value.data.localVersion ? 'OFFLINE' : value.data.status);
}

export const isTaskPseStatus = (value: unknown): value is TaskPseStatus => {
  return Object.values(TASK_PSE_STATUS).includes(value as TaskPseStatus);
}

export const getTaskPseStatus = (value: ValueGetterParams<Task>): string => {
  const status = value.data?.pseStatus;
  if (!status) {
    return '';
  }

  return isTaskPseStatus(status) ? "TASKS.PSE_STATUS." + status : "PSE_STATUS_UNKNOWN";
}

export const isPseSyncFailed = (task?: Task | null): boolean => {
  return !!task?.pseSyncStatus
    && task.pseSyncStatus !== TASK_PSE_SYNC_STATUS.NOT_PUSHED
    && task.pseSyncStatus !== TASK_PSE_SYNC_STATUS.TASK_STATUS_CHANGED;
}

export const getTaskType = (value: ValueGetterParams): string => {
  return "TASKS.TYPE." + value.data.type;
}

function calculateDistance(loc1: Location, loc2: Location) {
  return getDistance([loc1.longitude, loc1.latitude], [loc2.longitude, loc2.latitude]);
}

// Sortowanie lokalizacji według odległości od Twojej lokalizacji
export const sortTasksByLocation = (tasks: Task[], userLocation: Location): Task[]  => {
  tasks = tasks.map(item => {

    if (item.span) {
      const start = {
        latitude: item.span.startTower.latitude,
        longitude: item.span.startTower.longitude
      };

      const stop = {
        latitude: item.span.endTower.latitude,
        longitude: item.span.endTower.longitude
      };

      item.span.latitude = (start.latitude + stop.latitude) / 2;
      item.span.longitude = (start.longitude + stop.longitude) / 2;

    }

    return item;
  })

  return tasks.sort((a, b) => {
    const distanceA = calculateDistance(userLocation, a.span ? { longitude: a.span.longitude!, latitude: a.span.latitude! } : { longitude: a.tower?.longitude!, latitude: a.tower?.latitude! });
    const distanceB = calculateDistance(userLocation, b.span ? { longitude: b.span.longitude!, latitude: b.span.latitude! } : { longitude: b.tower?.longitude!, latitude: b.tower?.latitude! });
    return distanceA - distanceB;
  });
}

export function getTaskGps(task: Task): {longitude: number; latitude: number} {
  switch(task.type) {
    case 'TOWER':
      const tower = task.tower;
      return {
        longitude: tower?.longitude ?? 0,
        latitude: tower?.latitude ?? 0
      }
    case 'SPAN':
      const span = task.span;
      const lat1 = span?.startTower.latitude ?? 0;
      const lat2 = span?.endTower.latitude ?? 0;
      const lon1 = span?.startTower.longitude ?? 0;
      const lon2 = span?.endTower.longitude ?? 0;
      return {
        longitude: (lon1 + lon2) / 2,
        latitude: (lat1 + lat2) / 2
      }
    default:
      return {
        longitude: 0,
        latitude: 0
      }
  }
}

export const getTasksColumnDefs = (translate: TranslateService, checkboxSelection: (boolean | CheckboxSelectionCallback) = false) => [
  {
    colId: 'number',
    headerName: "NUMBER",
    valueGetter: ({ data }: ValueGetterParams) => data?.[data.type?.toLowerCase()]?.number || '',
    pinned: true,
    headerCheckboxSelection: !!checkboxSelection,
    checkboxSelection: checkboxSelection,
    headerCheckboxSelectionFilteredOnly: true,
  },
  {
    colId: 'name',
    field: "name",
    headerName: "NAME",
    comparator: (valueA: string, valueB: string) => valueA.toLowerCase().localeCompare(valueB.toLowerCase())
  },
  {
    colId: 'source',
    field: 'order.source',
    headerName: "SOURCE",
    hide: true,
  },
  {
    colId: 'type',
    field: "type",
    headerName: "TYPE",
    valueGetter: (value: ValueGetterParams) => translate.instant(getTaskType(value)),
    width: 125
  },
  {
    colId: 'skms',
    headerName: "SKMS",
    minWidth: 250,
    valueGetter: ({ data }: ValueGetterParams) => {
      if (!data) return '';

      const { type, tower, span } = data;

      switch (type as TaskType) {
        case 'TOWER':
          return tower.externalId;
        case 'SPAN':
          return getSKMSForSpan(span);
        default:
          return '';
      }
    }
  },
  {
    colId: 'status',
    field: "status",
    headerName: "STATUS",
    valueGetter: (value: ValueGetterParams) => translate.instant(getTaskStatus(value)),
    width: 125
  },
  {
    colId: 'pse_status',
    field: "pseStatus",
    headerName: "PSE_STATUS",
    valueGetter: (value: ValueGetterParams<Task>) => {
      const key = getTaskPseStatus(value);
      return key ? translate.instant(key) : '';
    },
    hide: true,
    width: 150
  },
  {
    colId: 'pse_sent_on',
    field: "pseSentOn",
    headerName: "PSE_SENT_ON",
    valueGetter: (value: ValueGetterParams<Task>) => value.data?.pseSentOn ? getLocalDate(value.data.pseSentOn, DATE_TIME_FORMAT) : '',
    width: 150
  },
  {
    colId: 'assigned_to_team',
    headerName: "ASSIGNED_TO_TEAM",
    valueGetter: (value: ValueGetterParams) => value.data.assignees.map((user: UserModel) => userOptionLabel(user)),
    hide: true
  },
  {
    colId: 'planned_execution_date',
    field: "plannedExecutionDate",
    headerName: "PLANNED_EXECUTION_DATE",
    width: 150
  },
  {
    colId: 'created_by',
    headerName: "CREATED_BY",
    valueGetter: (value: ValueGetterParams) => userOptionLabel(value.data.createdBy)
  },
  {
    colId: 'created_on',
    headerName: "CREATED_ON",
    valueGetter: (value: ValueGetterParams) => getLocalDate(value.data.createdOn, DATE_TIME_FORMAT)
  },
  {
    colId: 'assigned_to',
    headerName: "ASSIGNED_TO",
    valueGetter: (value: ValueGetterParams) => userOptionLabel(value.data.assignedTo)
  },
  {
    colId: 'assigned_on',
    headerName: "ASSIGNED_ON",
    valueGetter: (value: ValueGetterParams) => getLocalDate(value.data.assignedOn, DATE_TIME_FORMAT)
  },
  {
    colId: 'modified_by',
    headerName: "MODIFIED_BY",
    valueGetter: (value: ValueGetterParams) => userOptionLabel(value.data.modifiedBy)
  },
  {
    colId: 'modified_on',
    headerName: "MODIFIED_ON",
    valueGetter: (value: ValueGetterParams) => getLocalDate(value.data.modifiedOn, DATE_TIME_FORMAT)
  },
  {
    colId: 'filters',
    headerName: "FILTERS",
    valueGetter: (value: ValueGetterParams) =>  value.data.filters.map((filter: any) => `${filter.name}: ${filter.value}`)
  },
  {
    colId: 'order',
    headerName: "ORDER",
    cellRenderer: OrderCellComponent,
    valueGetter: (value: ValueGetterParams) =>  value.data.order.name
  },
  {
    colId: 'actions',
    headerName: ACTION_COLUMN,
    cellRenderer: TasksGridActionsComponent,
    filter: false,
    sortable: false,
    width: 300
  }
];

export const getSKMSForSpan = (span: SpanModel): string => {
  return span.externalId;
}
